import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { auth, users as usersApi, firmware as fwApi, type ManagedUser, type FirmwareRelease } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { useTenants } from '@/hooks/useActiveTenant';
import { relTime } from '@/lib/format';
import { Lock, UserPlus, KeyRound, Trash2, ToggleLeft, ToggleRight, Shield, Upload, Star, Globe } from 'lucide-react';

type Tab = 'profile' | 'users' | 'firmware' | 'dns_domains';

export default function Settings() {
  const { user } = useAuth();
  const canManageUsers = !!user && (user.is_superadmin || user.role === 'admin');
  const [tab, setTab] = useState<Tab>('profile');

  return (
    <>
      <PageHeader title="Paramètres" subtitle={user?.email ?? ''} />
      <div className="flex-1 overflow-auto">
        <div className="border-b border-border bg-bg-2 px-4 flex items-center gap-1">
          <TabBtn active={tab === 'profile'} onClick={() => setTab('profile')}>Mon profil</TabBtn>
          {canManageUsers && (
            <TabBtn active={tab === 'users'} onClick={() => setTab('users')}>Utilisateurs</TabBtn>
          )}
          {canManageUsers && (
            <TabBtn active={tab === 'firmware'} onClick={() => setTab('firmware')}>Firmware</TabBtn>
          )}
          {canManageUsers && (
            <TabBtn active={tab === 'dns_domains'} onClick={() => setTab('dns_domains')}>Domaines DNS testés</TabBtn>
          )}
        </div>
        <div className="p-6 max-w-6xl">
          {tab === 'profile' && <ProfileTab />}
          {tab === 'users' && canManageUsers && <UsersTab />}
          {tab === 'firmware' && canManageUsers && <FirmwareTab />}
          {tab === 'dns_domains' && canManageUsers && <DnsDomainsTab />}
        </div>
      </div>
    </>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-4 text-xs border-b-2 -mb-px ${active ? 'border-brand text-text' : 'border-transparent text-text-muted hover:text-text'}`}>
      {children}
    </button>
  );
}

// ─── Mon profil ──────────────────────────────────────────────────────────────
function ProfileTab() {
  const { user } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const m = useMutation({
    mutationFn: () => auth.changeMyPassword(current, next),
    onSuccess: () => {
      toast.push({ kind: 'success', title: 'Mot de passe mis à jour' });
      setCurrent(''); setNext(''); setConfirm('');
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } };
      const code = err?.response?.data?.error;
      toast.push({ kind: 'error', title: code === 'current_password_invalid' ? 'Mot de passe actuel incorrect' : 'Erreur lors du changement' });
    },
  });

  const valid = current.length >= 1 && next.length >= 10 && next === confirm && next !== current;

  return (
    <div className="space-y-6 max-w-md">
      <section>
        <h2 className="text-sm font-semibold text-text mb-3">Identité</h2>
        <div className="card p-4 space-y-2 text-xs">
          <Row label="Email" value={user?.email ?? ''} />
          <Row label="Nom" value={user?.name ?? '—'} />
          <Row label="Rôle" value={
            user?.is_superadmin ? <span className="badge bg-info/10 text-info border-info/30">superadmin</span>
              : user?.role ?? '—'
          } />
          <Row label="Tenant" value={user?.tenant?.name ?? '—'} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-text mb-3 flex items-center gap-2">
          <Lock size={13} />Changer mon mot de passe
        </h2>
        <div className="card p-4 space-y-3">
          <Field label="Mot de passe actuel" type="password" value={current} onChange={setCurrent} />
          <Field label="Nouveau mot de passe (min. 10 car., lettres + chiffres)" type="password" value={next} onChange={setNext} />
          <Field label="Confirmer" type="password" value={confirm} onChange={setConfirm} />
          {next && confirm && next !== confirm && (
            <p className="text-[11px] text-warning">Les deux mots de passe ne correspondent pas.</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              className="btn btn-primary btn-sm"
              disabled={!valid || m.isPending}
              onClick={() => m.mutate()}>
              {m.isPending ? 'Mise à jour…' : 'Mettre à jour'}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-text-faint mt-2">
          Les autres sessions seront déconnectées. Votre session actuelle reste active.
        </p>
      </section>
    </div>
  );
}

// ─── Utilisateurs (admin) ────────────────────────────────────────────────────
function UsersTab() {
  const { user: me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<ManagedUser | null>(null);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const toggleActive = useMutation({
    mutationFn: (u: ManagedUser) => usersApi.update(u.id, { is_active: !u.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const changeRole = useMutation({
    mutationFn: (p: { u: ManagedUser; role: 'admin' | 'viewer' }) => usersApi.update(p.u.id, { role: p.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const del = useMutation({
    mutationFn: (id: number) => usersApi.destroy(id),
    onSuccess: () => { toast.push({ kind: 'success', title: 'Utilisateur supprimé' }); qc.invalidateQueries({ queryKey: ['users'] }); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text">Utilisateurs ({list.length})</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
          <UserPlus size={12} />Nouvel utilisateur
        </button>
      </div>

      <table className="luc-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Nom</th>
            <th>Rôle</th>
            <th>Tenant</th>
            <th>Statut</th>
            <th>Dernière connexion</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {list.map((u) => {
            const isMe = u.id === me?.id;
            return (
              <tr key={u.id}>
                <td className="font-mono text-xs">{u.email}</td>
                <td className="text-xs text-text-muted">{u.name ?? '—'}</td>
                <td>
                  {u.is_superadmin
                    ? <span className="badge bg-info/10 text-info border-info/30 text-[10.5px]">superadmin</span>
                    : (
                      <select
                        className="input input-sm text-xs"
                        value={u.role}
                        disabled={isMe || (!me?.is_superadmin && u.is_superadmin)}
                        onChange={(e) => changeRole.mutate({ u, role: e.target.value as 'admin' | 'viewer' })}>
                        <option value="admin">admin</option>
                        <option value="viewer">viewer</option>
                      </select>
                    )}
                </td>
                <td className="text-xs text-text-muted">{u.tenant?.name ?? u.tenant_id}</td>
                <td>
                  <button
                    className={`btn btn-ghost btn-sm ${u.is_active ? 'text-success' : 'text-text-faint'}`}
                    disabled={isMe}
                    title={isMe ? 'Vous ne pouvez pas vous désactiver' : (u.is_active ? 'Désactiver' : 'Activer')}
                    onClick={() => toggleActive.mutate(u)}>
                    {u.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  </button>
                </td>
                <td className="text-xs text-text-muted">{relTime(u.last_login_at ?? null)}</td>
                <td>
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Forcer un nouveau mot de passe"
                      disabled={isMe || (!me?.is_superadmin && u.is_superadmin)}
                      onClick={() => setResetTarget(u)}>
                      <KeyRound size={12} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm text-danger"
                      title="Supprimer"
                      disabled={isMe || (!me?.is_superadmin && u.is_superadmin)}
                      onClick={() => { if (window.confirm(`Supprimer ${u.email} ?`)) del.mutate(u.id); }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {isLoading && <tr><td colSpan={7} className="text-center py-8 text-text-faint">Chargement…</td></tr>}
          {!isLoading && list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-text-faint">Aucun utilisateur.</td></tr>}
        </tbody>
      </table>

      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}
      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user: me } = useAuth();
  const { data: tenants = [] } = useTenants();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer');
  const [tenantId, setTenantId] = useState<number | undefined>(me?.tenant_id ?? undefined);

  const m = useMutation({
    mutationFn: () => usersApi.create({
      email, name: name || undefined, password, role,
      tenant_id: me?.is_superadmin ? tenantId : undefined,
    }),
    onSuccess: () => { toast.push({ kind: 'success', title: 'Utilisateur créé' }); qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err?.response?.data?.message ?? 'Erreur';
      toast.push({ kind: 'error', title: msg });
    },
  });

  const valid = /\S+@\S+\.\S+/.test(email) && password.length >= 10;

  return (
    <Modal open onClose={onClose} title="Nouvel utilisateur" width="max-w-md">
      <div className="space-y-3 p-4">
        <Field label="Email" value={email} onChange={setEmail} placeholder="user@filos.ca" />
        <Field label="Nom (optionnel)" value={name} onChange={setName} />
        <Field label="Mot de passe (min. 10 car., lettres + chiffres)" type="password" value={password} onChange={setPassword} />
        <div>
          <label className="block text-[11px] text-text-faint uppercase mb-1">Rôle</label>
          <select className="input w-full" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}>
            <option value="viewer">viewer (lecture seule)</option>
            <option value="admin">admin (gestion du tenant)</option>
          </select>
        </div>
        {me?.is_superadmin && (
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Tenant</label>
            <select className="input w-full" value={tenantId ?? ''} onChange={(e) => setTenantId(Number(e.target.value))}>
              <option value="">— sélectionner —</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-sm" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary btn-sm" disabled={!valid || m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: ManagedUser; onClose: () => void }) {
  const toast = useToast();
  const [pw, setPw] = useState('');
  const m = useMutation({
    mutationFn: () => usersApi.resetPassword(user.id, pw),
    onSuccess: () => { toast.push({ kind: 'success', title: `Mot de passe de ${user.email} réinitialisé. Sessions invalidées.` }); onClose(); },
    onError: () => toast.push({ kind: 'error', title: 'Erreur lors du reset' }),
  });
  return (
    <Modal open onClose={onClose} title={`Reset mot de passe — ${user.email}`} width="max-w-md">
      <div className="space-y-3 p-4">
        <p className="text-xs text-text-muted">
          Définissez un nouveau mot de passe pour cet utilisateur. Toutes ses sessions actuelles seront invalidées.
        </p>
        <Field label="Nouveau mot de passe (min. 10 car., lettres + chiffres)" type="password" value={pw} onChange={setPw} />
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-sm" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary btn-sm" disabled={pw.length < 10 || m.isPending} onClick={() => m.mutate()}>
            <Shield size={11} />{m.isPending ? 'Reset…' : 'Réinitialiser'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Firmware store (admin) ─────────────────────────────────────────────────
function FirmwareTab() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['firmware'],
    queryFn: () => fwApi.list(),
  });

  const promoteMut = useMutation({
    mutationFn: (id: number) => fwApi.promote(id),
    onSuccess: () => { toast.push({ kind: 'success', title: 'Firmware promu en officiel' }); qc.invalidateQueries({ queryKey: ['firmware'] }); },
  });
  const delMut = useMutation({
    mutationFn: (id: number) => fwApi.destroy(id),
    onSuccess: () => { toast.push({ kind: 'success', title: 'Firmware supprimé' }); qc.invalidateQueries({ queryKey: ['firmware'] }); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-text">Store de firmware ({list.length})</h2>
          <p className="text-[11px] text-text-muted mt-0.5">
            Officiels (visibles par tous) + privés du tenant courant. Push manuel vers un device depuis sa fiche.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
          <Upload size={12} />Uploader un firmware
        </button>
      </div>

      <table className="luc-table text-xs">
        <thead>
          <tr>
            <th>Scope</th>
            <th>Vendor</th>
            <th>Modèle</th>
            <th>Version</th>
            <th>Taille</th>
            <th>SHA256</th>
            <th>Uploadé</th>
            <th>Par</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {list.map((f) => (
            <tr key={f.id}>
              <td>
                {f.tenant_id === null
                  ? <span className="badge bg-info/10 text-info border-info/30 text-[10.5px]"><Globe size={10} /> officiel</span>
                  : <span className="badge bg-bg-2 text-text-muted border-border text-[10.5px]">{f.tenant?.slug ?? `tenant-${f.tenant_id}`}</span>}
              </td>
              <td className="font-mono">{f.vendor}</td>
              <td className="font-mono">{f.model}</td>
              <td className="font-mono">{f.version}</td>
              <td className="text-text-muted">{f.file_size ? fmtSize(f.file_size) : '—'}</td>
              <td className="font-mono text-[10.5px] text-text-faint" title={f.sha256 ?? ''}>{f.sha256?.slice(0, 12) ?? '—'}…</td>
              <td className="text-text-muted">{relTime(f.uploaded_at)}</td>
              <td className="text-text-muted">{f.uploader?.email ?? '—'}</td>
              <td>
                <div className="flex items-center gap-1 justify-end">
                  {me?.is_superadmin && f.tenant_id !== null && (
                    <button
                      className="btn btn-ghost btn-sm text-info"
                      title="Promouvoir en officiel (visible par tous les tenants)"
                      onClick={() => { if (window.confirm(`Promouvoir ${f.vendor} ${f.model} ${f.version} en officiel ?`)) promoteMut.mutate(f.id); }}>
                      <Star size={12} />
                    </button>
                  )}
                  <button
                    className="btn btn-ghost btn-sm text-danger"
                    title="Supprimer"
                    onClick={() => { if (window.confirm(`Supprimer ${f.vendor} ${f.model} ${f.version} ?`)) delMut.mutate(f.id); }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {isLoading && <tr><td colSpan={9} className="text-center py-8 text-text-faint">Chargement…</td></tr>}
          {!isLoading && list.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-text-faint">Aucun firmware dans le store.</td></tr>}
        </tbody>
      </table>

      {uploadOpen && <UploadFirmwareModal onClose={() => setUploadOpen(false)} />}
    </div>
  );
}

function UploadFirmwareModal({ onClose }: { onClose: () => void }) {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [hwVersions, setHwVersions] = useState('');
  const [notes, setNotes] = useState('');
  const [official, setOfficial] = useState(false);
  const [progress, setProgress] = useState(0);

  const m = useMutation({
    mutationFn: () => {
      setProgress(0);
      const fd = new FormData();
      fd.append('vendor', vendor);
      fd.append('model', model);
      fd.append('version', version);
      if (file) fd.append('file', file);
      if (hwVersions.trim()) {
        hwVersions.split(',').map(s => s.trim()).filter(Boolean).forEach((v) => fd.append('hw_versions[]', v));
      }
      if (notes.trim()) fd.append('notes', notes);
      if (me?.is_superadmin && official) fd.append('official', '1');
      return fwApi.upload(fd, setProgress);
    },
    onSuccess: () => {
      toast.push({ kind: 'success', title: 'Firmware uploadé' });
      qc.invalidateQueries({ queryKey: ['firmware'] });
      onClose();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err?.response?.data?.message ?? 'Erreur upload';
      toast.push({ kind: 'error', title: msg });
    },
  });

  const valid = vendor.trim() && model.trim() && version.trim() && file;

  return (
    <Modal open onClose={onClose} title="Uploader un firmware" width="max-w-lg">
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Vendor</label>
            <input className="input w-full font-mono" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="calix" />
          </div>
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Modèle</label>
            <input className="input w-full font-mono" value={model} onChange={(e) => setModel(e.target.value)} placeholder="844E ou GigaSpire" />
          </div>
          <div>
            <label className="block text-[11px] text-text-faint uppercase mb-1">Version</label>
            <input className="input w-full font-mono" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="24.1.0" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] text-text-faint uppercase mb-1">Fichier (.bin / .pkg / ...)</label>
          <input type="file" className="w-full text-xs" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {file && <p className="text-[11px] text-text-muted mt-1">{file.name} — {fmtSize(file.size)}</p>}
        </div>
        <div>
          <label className="block text-[11px] text-text-faint uppercase mb-1">Versions HW compatibles (séparées par virgule)</label>
          <input className="input w-full font-mono" value={hwVersions} onChange={(e) => setHwVersions(e.target.value)} placeholder="3.x, 4.x" />
        </div>
        <div>
          <label className="block text-[11px] text-text-faint uppercase mb-1">Notes / changelog</label>
          <textarea className="input w-full" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Release notes…" />
        </div>
        {me?.is_superadmin && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={official} onChange={(e) => setOfficial(e.target.checked)} />
            <span>Pool <strong>officiel</strong> (visible par tous les tenants)</span>
          </label>
        )}
        <p className="text-[11px] text-text-faint">SHA256 calculé automatiquement à l'upload. Re-vérifié avant chaque push device.</p>
        {m.isPending && (
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] text-text-muted">
              <span>{progress < 100 ? 'Upload en cours…' : 'Traitement serveur (SHA256, écriture disque)…'}</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div className="h-2 bg-bg-2 border border-border rounded overflow-hidden">
              <div className="h-full bg-brand transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            {file && progress > 0 && progress < 100 && (
              <p className="text-[10.5px] text-text-faint">
                {fmtSize(Math.round((progress / 100) * file.size))} / {fmtSize(file.size)}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn btn-sm" onClick={onClose} disabled={m.isPending}>Annuler</button>
          <button className="btn btn-primary btn-sm" disabled={!valid || m.isPending} onClick={() => m.mutate()}>
            <Upload size={11} />{m.isPending ? `Upload ${progress}%` : 'Uploader'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface DnsTestDomain {
  id: number; domain: string; category: string | null; enabled: boolean; notes: string | null;
}

function DnsDomainsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newCategory, setNewCategory] = useState('');

  const { data } = useQuery({
    queryKey: ['dns-test-domains'],
    queryFn: async () => {
      const r = await fetch('/api/v1/dns-test-domains', {
        headers: { 'Accept': 'application/json', 'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '') },
        credentials: 'include',
      });
      const j = await r.json();
      return j.data as DnsTestDomain[];
    },
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, enabled }: { id: number; enabled: boolean }) => {
      const r = await fetch(`/api/v1/dns-test-domains/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json',
                   'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '') },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dns-test-domains'] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/v1/dns-test-domains/${id}`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Accept': 'application/json', 'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '') },
      });
      if (!r.ok) throw new Error(`${r.status}`);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dns-test-domains'] }); toast.push({ kind: 'success', title: 'Domaine supprimé' }); },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/v1/dns-test-domains', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json',
                   'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '') },
        body: JSON.stringify({ domain: newDomain.trim(), category: newCategory.trim() || null, enabled: true }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.message ?? `${r.status}`); }
    },
    onSuccess: () => { setNewDomain(''); setNewCategory(''); qc.invalidateQueries({ queryKey: ['dns-test-domains'] }); toast.push({ kind: 'success', title: 'Domaine ajouté' }); },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Erreur · ' + e.message }),
  });

  const rows = (data ?? []).filter((d) => d.domain.toLowerCase().includes(filter.toLowerCase()) || (d.category ?? '').toLowerCase().includes(filter.toLowerCase()));
  const byCategory: Record<string, number> = {};
  (data ?? []).forEach((d) => { if (d.enabled) byCategory[d.category ?? 'other'] = (byCategory[d.category ?? 'other'] ?? 0) + 1; });

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h3 className="text-sm font-semibold mb-2">Domaines testés par les DNS endpoints</h3>
        <p className="text-xs text-text-muted mb-3">
          Le service `dns_query` rotate parmi ces domaines (1 par minute par DNS endpoint). Un domaine désactivé n'est plus testé.
          La liste est cachée 5min, les changements s'appliquent au prochain refresh cache.
        </p>
        <div className="text-[11px] text-text-faint flex flex-wrap gap-3">
          {Object.entries(byCategory).map(([cat, c]) => (
            <span key={cat}><b className="text-text">{c}</b> {cat}</span>
          ))}
          <span>· total enabled : <b className="text-text">{(data ?? []).filter((d) => d.enabled).length}</b> / {(data ?? []).length}</span>
        </div>
      </div>

      <div className="card p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-faint mb-2">+ Ajouter un domaine</h4>
        <div className="flex items-center gap-2">
          <input className="input flex-1" placeholder="example.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
          <input className="input w-40" placeholder="catégorie (optionnel)" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
          <button className="btn btn-primary btn-sm" disabled={!newDomain.trim() || addMut.isPending}
                  onClick={() => addMut.mutate()}>Ajouter</button>
        </div>
      </div>

      <div className="card">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <input className="input w-72 text-xs" placeholder="Filtrer domaine / catégorie…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <span className="text-xs text-text-faint">{rows.length} affichés</span>
        </div>
        <table className="luc-table text-xs">
          <thead><tr><th>Domaine</th><th>Catégorie</th><th>Activé</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td className="font-mono">{d.domain}</td>
                <td><span className="badge text-[10px]">{d.category ?? '—'}</span></td>
                <td>
                  <button onClick={() => toggleMut.mutate({ id: d.id, enabled: !d.enabled })}
                    className={`btn btn-sm ${d.enabled ? 'text-success' : 'text-text-faint'}`}>
                    {d.enabled ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}{d.enabled ? 'on' : 'off'}
                  </button>
                </td>
                <td className="text-text-muted">{d.notes ?? '—'}</td>
                <td>
                  <button className="btn btn-sm text-danger"
                          onClick={() => { if (confirm(`Supprimer ${d.domain} ?`)) deleteMut.mutate(d.id); }}>
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-text-faint italic">Aucun domaine — utilise le formulaire en haut.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

// ─── helpers ────────────────────────────────────────────────────────────────
function Field({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div>
      <label className="block text-[11px] text-text-faint uppercase mb-1">{label}</label>
      <input
        className="input w-full"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={type === 'password' ? 'new-password' : 'off'}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[100px_1fr] items-center gap-2 py-1">
      <span className="text-text-faint">{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}
